import {
  AlignCenterHorizontal, AlignCenterVertical, AlignEndHorizontal, AlignEndVertical, AlignHorizontalDistributeCenter,
  AlignStartHorizontal, AlignStartVertical, AlignVerticalDistributeCenter, ArrowDownToLine, ArrowUpToLine, Bold, BringToFront,
  Copy, Group, Italic, Lock, LockOpen, SendToBack, Trash2, Ungroup, PanelRightClose, AlignLeft, AlignCenter, AlignRight
} from "lucide-react";
import { useState } from "react";
import type {
  Align, CardElement, Dash, FontKey, FrameElement, HeadKind, ImageElement, InkElement, LineElement, NoteElement, Route, SepiaElement,
  ShapeElement, TextElement
} from "../../shared/model";
import { useBoard, useEditor, useStore, useUi } from "../editor/react/hooks";
import { FILLS, NOTE_PAPERS, PAPERS, TAPES } from "../editor/style";
import { measureText } from "../editor/measure";
import { Chip, Dot, IconButton, Panel, Row, Section, Stepper, WavySlider } from "../ui/kit";
import {
  DashGlyph, FILL_STYLES, FillGlyph, FontChips, HeadGlyph, InkMarkers, ROUTES, SHAPE_KINDS, ShapeGlyph
} from "./ToolOptions";

const TYPE_NAMES: Record<SepiaElement["type"], string> = {
  ink: "Ink", shape: "Shape", line: "Connector", text: "Text", note: "Sticky note", card: "Index card", image: "Photo", frame: "Page frame", sticker: "Sticker", layer: "Layer", page: "Page"
};

function NumField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <label className="num-field">
      <span>{label}</span>
      <input
        inputMode="decimal"
        value={draft ?? String(Math.round(value * 10) / 10)}
        onFocus={(e) => {
          setDraft(e.target.value);
          e.target.select();
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const v = Number(draft);
          if (draft !== null && Number.isFinite(v)) onChange(v);
          setDraft(null);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
    </label>
  );
}

export function Inspector({ side, onClose }: { side: "left" | "right"; onClose: () => void }) {
  const ed = useEditor();
  const busy = useUi((ui) => ui.busy);
  const readOnly = useUi((ui) => ui.readOnly);
  const dark = useUi((_, e) => !!PAPERS[e.bg.paper]?.dark);
  const sub = (fn: () => void) => {
    const offs = [ed.subscribeUi(fn), ed.doc.subscribe(fn)];
    return () => offs.forEach((o) => o());
  };
  const sel = useStore(sub, () => ed.selected(), (a, b) => a.length === b.length && a.every((x, i) => x === b[i]));
  if (!sel.length || busy || readOnly) return null;

  const types = new Set(sel.map((e) => e.type));
  const only = types.size === 1 ? sel[0].type : null;
  const one = sel.length === 1 ? sel[0] : null;
  const upd = (fn: (e: SepiaElement) => SepiaElement | null, key?: string) => ed.updateSelected(fn, key);
  const locked = sel.every((e) => e.locked);

  const inks = sel.filter((e): e is InkElement => e.type === "ink");
  const shapes = sel.filter((e): e is ShapeElement => e.type === "shape");
  const lines = sel.filter((e): e is LineElement => e.type === "line");
  const notes = sel.filter((e): e is NoteElement => e.type === "note");
  const cards = sel.filter((e): e is CardElement => e.type === "card");
  const texts = sel.filter((e): e is TextElement => e.type === "text");
  const images = sel.filter((e): e is ImageElement => e.type === "image");
  const frames = sel.filter((e): e is FrameElement => e.type === "frame");
  const strokeColor = inks[0]?.color ?? shapes[0]?.stroke ?? lines[0]?.stroke ?? texts[0]?.color;
  const hasStroke = inks.length + shapes.length + lines.length + texts.length > 0;
  const texty = [...texts, ...notes, ...shapes, ...lines];
  const firstTexty = texty[0] as (TextElement | NoteElement | ShapeElement | LineElement) | undefined;

  const setColor = (c: string) =>
    upd((e) => {
      if (e.type === "ink") return { ...e, color: c };
      if (e.type === "shape") return { ...e, stroke: c, textColor: e.fillStyle === "solid" ? e.textColor : c };
      if (e.type === "line") return { ...e, stroke: c };
      if (e.type === "text") return { ...e, color: c };
      return null;
    });

  const remeasure = (e: SepiaElement): SepiaElement => {
    if (e.type !== "text") return e;
    const m = measureText(e.text, e.font, e.fontSize, { maxWidth: e.autoW ? undefined : e.w, bold: e.bold, italic: e.italic });
    return { ...e, w: e.autoW ? Math.max(20, m.w) : e.w, h: Math.max(m.h, e.fontSize) };
  };

  return (
    <Panel className={"inspector inspector-" + side} seed="inspector" aria-label="Selection">
      <div className="insp-title">
        <span className="insp-kind">{only ? TYPE_NAMES[only] : "Mixed"}</span>
        <span className="insp-count">{sel.length} selected</span>
        <IconButton icon={PanelRightClose} label="Hide panel" size={18} onClick={onClose} className="insp-close" />
      </div>
      <div className="insp-scroll">
        {one && one.type !== "line" && !locked && (
          <Section title="Position" id="position" defaultOpen={false}>
            <div className="num-grid">
              <NumField label="x" value={one.x} onChange={(v) => ed.update(one.id, (e) => ({ ...e, x: v }))} />
              <NumField label="y" value={one.y} onChange={(v) => ed.update(one.id, (e) => ({ ...e, y: v }))} />
              <NumField label="w" value={one.w} onChange={(v) => v > 1 && ed.transformSelected({ kind: "scale", sx: v / one.w, sy: 1, origin: { x: one.x, y: one.y }, frameRot: one.rot })} />
              <NumField label="h" value={one.h} onChange={(v) => v > 1 && ed.transformSelected({ kind: "scale", sx: 1, sy: v / one.h, origin: { x: one.x, y: one.y }, frameRot: one.rot })} />
              <NumField label="°" value={(one.rot * 180) / Math.PI} onChange={(v) => ed.update(one.id, (e) => ({ ...e, rot: (v * Math.PI) / 180 }))} />
            </div>
          </Section>
        )}

        {!locked && (
          <Section title="Style" id="style">
            {hasStroke && (
              <Row label="ink">
                <InkMarkers value={strokeColor ?? "#2b211a"} onChange={setColor} dark={dark} />
              </Row>
            )}
            {inks.length > 0 && (
              <Row label="size">
                <Stepper
                  value={inks[0].size}
                  min={0.5}
                  max={120}
                  step={inks[0].size < 4 ? 0.5 : 2}
                  label="stroke size"
                  format={(v) => v.toFixed(v < 10 ? 1 : 0)}
                  onChange={(v) => upd((e) => (e.type === "ink" ? { ...e, size: Math.max(0.5, e.size * (v / inks[0].size)) } : null))}
                />
              </Row>
            )}
            {only === "shape" && one && (
              <Row label="shape">
                <div className="shape-kinds compact">
                  {SHAPE_KINDS.map((s) => (
                    <button key={s.kind} type="button" className={"glyph-btn" + ((one as ShapeElement).kind === s.kind ? " active" : "")} onClick={() => upd((e) => (e.type === "shape" ? { ...e, kind: s.kind } : null))} aria-label={s.label} data-tip={s.label}>
                      <ShapeGlyph kind={s.kind} />
                    </button>
                  ))}
                </div>
              </Row>
            )}
            {shapes.length > 0 && (
              <>
                <Row label="fill">
                  <div className="glyph-row">
                    {FILL_STYLES.map((f) => (
                      <button key={f.key} type="button" className={"glyph-btn" + (shapes[0].fillStyle === f.key ? " active" : "")} onClick={() => upd((e) => (e.type === "shape" ? { ...e, fillStyle: f.key } : null))} aria-label={f.label} data-tip={f.label}>
                        <FillGlyph kind={f.key} />
                      </button>
                    ))}
                  </div>
                </Row>
                {shapes[0].fillStyle !== "none" && (
                  <Row label="colour">
                    <div className="dots wrap">
                      {FILLS.map((f) => (
                        <Dot key={f.color} color={f.color} label={f.name} active={shapes[0].fill === f.color} onClick={() => upd((e) => (e.type === "shape" ? { ...e, fill: f.color, textColor: e.fillStyle === "solid" && f.color === "#8b6a52" ? "#fbf6ec" : e.textColor } : null))} />
                      ))}
                    </div>
                  </Row>
                )}
              </>
            )}
            {(shapes.length > 0 || lines.length > 0) && (
              <>
                <Row label="line">
                  <div className="size-dots">
                    {[1.2, 2.2, 3.6, 5.5].map((w) => (
                      <button key={w} type="button" className={"size-dot" + (((shapes[0] ?? lines[0]) as ShapeElement).strokeWidth === w ? " active" : "")} onClick={() => upd((e) => (e.type === "shape" || e.type === "line" ? { ...e, strokeWidth: w } : null))} aria-label={"Line " + w}>
                        <span style={{ width: 3 + w * 2.4, height: 3 + w * 2.4 }} />
                      </button>
                    ))}
                  </div>
                  <div className="glyph-row">
                    {(["solid", "dashed", "dotted"] as Dash[]).map((d) => (
                      <button key={d} type="button" className={"glyph-btn" + (((shapes[0] ?? lines[0]) as ShapeElement).dash === d ? " active" : "")} onClick={() => upd((e) => (e.type === "shape" || e.type === "line" ? { ...e, dash: d } : null))} aria-label={d}>
                        <DashGlyph dash={d} />
                      </button>
                    ))}
                  </div>
                </Row>
                <Row label="neatness">
                  <WavySlider
                    value={((shapes[0] ?? lines[0]) as ShapeElement).rough}
                    onChange={(v) => upd((e) => (e.type === "shape" || e.type === "line" ? { ...e, rough: v } : null), "rough")}
                    label="Neatness"
                    left="neat"
                    right="scrawl"
                  />
                </Row>
              </>
            )}
            {lines.length > 0 && (
              <>
                <Row label="route">
                  <div className="glyph-row">
                    {ROUTES.map((r) => (
                      <button key={r.key} type="button" className={"glyph-btn" + (lines[0].route === r.key ? " active" : "")} onClick={() => upd((e) => (e.type === "line" ? setRoute(e, r.key) : null))} aria-label={r.label} data-tip={r.label}>
                        <svg viewBox="0 0 28 20" width="28" height="20" aria-hidden="true">
                          <path d={r.d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ))}
                  </div>
                </Row>
                <Row label="ends">
                  <div className="glyph-row">
                    {(["none", "arrow", "triangle", "dot", "bar", "diamond"] as HeadKind[]).map((h) => (
                      <button key={"s" + h} type="button" className={"glyph-btn" + (lines[0].start.head === h ? " active" : "")} onClick={() => upd((e) => (e.type === "line" ? { ...e, start: { ...e.start, head: h } } : null))} aria-label={"Start " + h}>
                        <HeadGlyph head={h} flip />
                      </button>
                    ))}
                  </div>
                  <div className="glyph-row">
                    {(["none", "arrow", "triangle", "dot", "bar", "diamond"] as HeadKind[]).map((h) => (
                      <button key={"e" + h} type="button" className={"glyph-btn" + (lines[0].end.head === h ? " active" : "")} onClick={() => upd((e) => (e.type === "line" ? { ...e, end: { ...e.end, head: h } } : null))} aria-label={"End " + h}>
                        <HeadGlyph head={h} />
                      </button>
                    ))}
                  </div>
                </Row>
              </>
            )}
            {notes.length > 0 && (
              <>
                <Row label="paper">
                  <div className="dots wrap">
                    {NOTE_PAPERS.map((p) => (
                      <Dot key={p.color} color={p.color} label={p.name} active={notes[0].paper === p.color} onClick={() => upd((e) => (e.type === "note" ? { ...e, paper: p.color } : null))} />
                    ))}
                  </div>
                </Row>
                <Row label="tape">
                  <div className="dots wrap">
                    <Chip active={!notes[0].tape} onClick={() => upd((e) => (e.type === "note" ? { ...e, tape: null } : null))}>none</Chip>
                    {TAPES.map((t) => (
                      <Dot key={t.color} color={t.color} label={t.name} ring active={notes[0].tape === t.color} onClick={() => upd((e) => (e.type === "note" ? { ...e, tape: t.color } : null))} />
                    ))}
                  </div>
                </Row>
              </>
            )}
            {cards.length > 0 && (
              <>
                <Row label="paper">
                  <div className="dots wrap">
                    {["#fbf6ea", "#fdfbf6", "#f3e6cf", "#e9efe0", "#f6e6e0", "#e6edf3"].map((c) => (
                      <Dot key={c} color={c} label="Card paper" active={cards[0].paper === c} onClick={() => upd((e) => (e.type === "card" ? { ...e, paper: c } : null))} />
                    ))}
                  </div>
                </Row>
                <Row label="margin">
                  <div className="dots wrap">
                    {["#c2583a", "#cf9234", "#4f8a5b", "#3f64a0", "#7d4a86", "#6f6760"].map((c) => (
                      <Dot key={c} color={c} label="Margin line" active={cards[0].accent === c} onClick={() => upd((e) => (e.type === "card" ? { ...e, accent: c } : null))} />
                    ))}
                  </div>
                </Row>
                <Row label="size">
                  <Stepper value={cards[0].fontSize} min={11} max={40} step={1} label="card text size" onChange={(v) => upd((e) => (e.type === "card" ? { ...e, fontSize: v } : null))} />
                  <button type="button" className="btn btn-plain small" onClick={() => fitCardHeight(ed, cards)}>fit height</button>
                </Row>
              </>
            )}
            {images.length > 0 && (
              <Row label="frame">
                <div className="chips">
                  {(["none", "polaroid", "tape"] as const).map((f) => (
                    <Chip key={f} active={images[0].frame === f} onClick={() => upd((e) => (e.type === "image" ? { ...e, frame: f } : null))}>
                      {f === "none" ? "plain" : f}
                    </Chip>
                  ))}
                </div>
              </Row>
            )}
            {frames.length > 0 && (
              <Row label="colour">
                <div className="dots wrap">
                  {["#d9b25a", "#d0845f", "#93ad86", "#8aa3bf", "#ab9ac7", "#b7a28e"].map((c) => (
                    <Dot key={c} color={c} label="Frame colour" active={frames[0].color === c} onClick={() => upd((e) => (e.type === "frame" ? { ...e, color: c } : null))} />
                  ))}
                </div>
              </Row>
            )}
            <Row label="opacity">
              <WavySlider value={sel[0].opacity ?? 1} min={0.1} max={1} onChange={(v) => upd((e) => ({ ...e, opacity: v }), "opacity")} label="Opacity" format={(v) => Math.round(v * 100) + "%"} />
            </Row>
            {(notes.length > 0 || cards.length > 0 || shapes.length > 0) && (
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={!!((notes[0] ?? cards[0] ?? shapes[0]) as NoteElement).shadow || (notes[0] ?? cards[0])?.shadow === undefined && (notes.length > 0 || cards.length > 0)}
                  onChange={(e) => {
                    const v = e.target.checked;
                    upd((el) => (el.type === "note" || el.type === "card" || el.type === "shape" ? { ...el, shadow: v } : null));
                  }}
                />
                <span>lift off the page</span>
              </label>
            )}
          </Section>
        )}

        {firstTexty && !locked && (
          <Section title="Text" id="text">
            <Row label="font">
              <FontChips value={(firstTexty as TextElement).font} onChange={(f: FontKey) => upd((e) => (e.type === "text" || e.type === "note" || e.type === "shape" || e.type === "line" ? remeasure({ ...e, font: f } as SepiaElement) : null))} />
            </Row>
            <Row label="size">
              <Stepper
                value={(firstTexty as TextElement).fontSize}
                min={6}
                max={240}
                step={(firstTexty as TextElement).fontSize >= 40 ? 4 : 2}
                label="text size"
                onChange={(v) => upd((e) => (e.type === "text" || e.type === "note" || e.type === "shape" || e.type === "line" ? remeasure({ ...e, fontSize: v } as SepiaElement) : null))}
              />
              {firstTexty.type !== "line" && (
                <div className="glyph-row">
                  {([["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]] as [Align, typeof AlignLeft][]).map(([a, I]) => (
                    <IconButton key={a} icon={I} size={17} label={"Align " + a} active={(firstTexty as TextElement).align === a} onClick={() => upd((e) => ("align" in e ? ({ ...e, align: a } as SepiaElement) : null))} />
                  ))}
                </div>
              )}
            </Row>
            {texts.length > 0 && (
              <Row label="style">
                <IconButton icon={Bold} size={17} label="Bold" active={!!texts[0].bold} onClick={() => upd((e) => (e.type === "text" ? remeasure({ ...e, bold: !texts[0].bold }) : null))} />
                <IconButton icon={Italic} size={17} label="Italic" active={!!texts[0].italic} onClick={() => upd((e) => (e.type === "text" ? remeasure({ ...e, italic: !texts[0].italic }) : null))} />
              </Row>
            )}
            {(notes.length > 0 || shapes.length > 0) && (
              <Row label="colour">
                <InkMarkers value={(notes[0]?.textColor ?? shapes[0]?.textColor) || "#2b211a"} onChange={(c) => upd((e) => (e.type === "note" || e.type === "shape" ? { ...e, textColor: c } : null))} dark={false} />
              </Row>
            )}
          </Section>
        )}

        <Section title="Arrange" id="arrange">
          <LayerPicker />
          <div className="arrange-grid">
            <IconButton icon={BringToFront} label="Bring to front" shortcut="⇧⌘]" onClick={() => ed.reorder("front")} />
            <IconButton icon={ArrowUpToLine} label="Bring forward" shortcut="⌘]" onClick={() => ed.reorder("forward")} />
            <IconButton icon={ArrowDownToLine} label="Send backward" shortcut="⌘[" onClick={() => ed.reorder("backward")} />
            <IconButton icon={SendToBack} label="Send to back" shortcut="⇧⌘[" onClick={() => ed.reorder("back")} />
            <IconButton icon={locked ? LockOpen : Lock} label={locked ? "Unlock" : "Lock"} shortcut="⇧⌘L" onClick={() => ed.toggleLock()} />
            {sel.length > 1 && !sel.some((e) => e.groupId) && <IconButton icon={Group} label="Group" shortcut="⌘G" onClick={() => ed.group()} />}
            {sel.some((e) => e.groupId) && <IconButton icon={Ungroup} label="Ungroup" shortcut="⇧⌘G" onClick={() => ed.ungroup()} />}
            <IconButton icon={Copy} label="Duplicate" shortcut="⌘D" onClick={() => ed.duplicateSelected()} />
            <IconButton icon={Trash2} label="Delete" shortcut="⌫" onClick={() => ed.deleteSelected()} className="danger" />
          </div>
          {sel.length > 1 && !locked && (
            <div className="arrange-grid">
              <IconButton icon={AlignStartVertical} label="Align left" onClick={() => ed.align("left")} />
              <IconButton icon={AlignCenterVertical} label="Align centres" onClick={() => ed.align("hcenter")} />
              <IconButton icon={AlignEndVertical} label="Align right" onClick={() => ed.align("right")} />
              <IconButton icon={AlignStartHorizontal} label="Align top" onClick={() => ed.align("top")} />
              <IconButton icon={AlignCenterHorizontal} label="Align middles" onClick={() => ed.align("vcenter")} />
              <IconButton icon={AlignEndHorizontal} label="Align bottom" onClick={() => ed.align("bottom")} />
              {sel.length > 2 && <IconButton icon={AlignHorizontalDistributeCenter} label="Space evenly across" onClick={() => ed.distribute("x")} />}
              {sel.length > 2 && <IconButton icon={AlignVerticalDistributeCenter} label="Space evenly down" onClick={() => ed.distribute("y")} />}
            </div>
          )}
        </Section>
      </div>
    </Panel>
  );
}

/** Move the selection to another layer (only shown once the board has more than one). */
function LayerPicker() {
  const ed = useEditor();
  const layers = useBoard((_, e) => e.layers());
  const current = useBoard((ui, e) => {
    const ids = new Set(ui.selection.filter((id) => e.doc.has(id)).map((id) => e.layerOf(e.doc.get(id)!)));
    return ids.size === 1 ? [...ids][0] : "";
  });
  if (layers.length < 2) return null;
  return (
    <Row label="layer">
      <label className="blend-select wide">
        <span className="sr-only">Layer</span>
        <select value={current} onChange={(e) => e.target.value && ed.moveSelectionToLayer(e.target.value)}>
          {!current && <option value="">several layers</option>}
          {[...layers].reverse().map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </label>
    </Row>
  );
}

function setRoute(e: LineElement, route: Route): LineElement {
  if (route === e.route) return e;
  const a = e.pts[0];
  const b = e.pts[e.pts.length - 1];
  if (route === "curve" && e.pts.length === 2) {
    const mid: [number, number] = [(a[0] + b[0]) / 2 - (b[1] - a[1]) * 0.18, (a[1] + b[1]) / 2 + (b[0] - a[0]) * 0.18];
    return { ...e, route, pts: [a, mid, b] };
  }
  return { ...e, route, pts: route === "curve" ? e.pts : [a, b] };
}

function fitCardHeight(ed: ReturnType<typeof useEditor>, cards: CardElement[]): void {
  const puts: SepiaElement[] = [];
  for (const c of cards) {
    const node = document.querySelector(`[data-id="${c.id}"] .card-body`) as HTMLElement | null;
    if (!node) continue;
    const need = node.scrollHeight + 6;
    if (Math.abs(need - c.h) > 2) puts.push({ ...c, h: Math.max(80, need), t: Date.now() });
  }
  if (puts.length) ed.commit({ puts });
}
